import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// UTILS
import { validationErrorsToString } from '../../utils';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { OrganisationNumberOfChildrenCommand } from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface ModalType {
	numberOfChildren?: number;
	getSettingsData: () => Promise<void>;
	closeModal?: closeModalType;
}

const UpdateNumberOfChildrenModal: React.FC<ModalType> = ({ numberOfChildren, closeModal, getSettingsData }) => {
	// Get Website URL
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [showInputError, setShowInputError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	// Set up Number input state
	const [inputValue, setInputValue] = React.useState<string>(numberOfChildren?.toString() || '');

	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};

	// Update number of Children
	const updateNumberOfChildren = async (values: any) => {
		if (!inputValue || parseInt(inputValue) < 50) {
			setShowInputError(true);
		} else {
			setShowInputError(false);

			setIsLoading(true);

			const data: OrganisationNumberOfChildrenCommand = { numberOfChildren: parseInt(inputValue) };

			const response = await apibridge.updateNumberOfChildren(data);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				if (closeModal) {
					closeModal();
				}

				getSettingsData();
			}
		}
	};

	return (
		<>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<div className="general-form mtop-0">
								<div className="form-group">
									<p className="text-subtitle">
										Thank you for updating the number of children in your organisation. ChildAware will charge your
										account for these additional children in accordance with the Customer{' '}
										<a className="subtitle-link" href={`${websiteUrl}/terms-conditions-customer`} target="_blank">
											Terms and Conditions
										</a>{' '}
										. Please note that you cannot reduce the number of children in your organisation during the Term of
										your agreement.
									</p>
									<div className="form-field">
										<input
											type="number"
											name="numberOfChildrenInput"
											id="numberOfChildrenInput"
											className="form-input"
											value={inputValue}
											onChange={onChangeHandler}
										/>

										{showInputError && <div className="error-message">A minimum of 50 children is required</div>}
									</div>
								</div>

								<div className="form-button-group w-100">
									<Button onClick={updateNumberOfChildren} className="btn-primary w-100">
										Update
									</Button>
								</div>
							</div>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</>
	);
};

export default UpdateNumberOfChildrenModal;
